import { OnboardingFlagKey } from "@redotech/feature-flags-keys-onboarding/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useExpiringLocalStorage } from "@redotech/react-util/local-storage";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right_filled.svg";
import CursorClickIcon from "@redotech/redo-icons/cursor-click-01.svg";
import KeyboardIcon from "@redotech/redo-icons/keyboard-01.svg";
import LogOut04 from "@redotech/redo-icons/log-out-04.svg";
import TeamMemberIcon from "@redotech/redo-icons/user-01.svg";
import {
  AuthContext,
  teamAuthStore,
  TOKEN_KEY,
} from "@redotech/redo-merchant-app-common/auth";
import { getUserAvatarUrl } from "@redotech/redo-merchant-app-common/get-avatar-url";
import { useMerchantRoutes } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { UserCallAvailabilityContext } from "@redotech/redo-merchant-support-app/services/user-call-availability";
import { UserCallAvailability } from "@redotech/redo-support-common/support/voice/voice-types";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { StatusDotColor } from "@redotech/redo-web/arbiter-components/list/redo-list-item-lead";
import {
  NavigationUserProfile,
  UserProfileContext,
} from "@redotech/redo-web/arbiter-components/navigation/common/redo-nav-user-profile";
import { DEVICE_AUTHENTICATION_TOKEN_KEY } from "@redotech/redo-web/authentication/authentication-settings";
import { globalShortcuts } from "@redotech/redo-web/keyboard-shortcuts/global-shortcuts";
import { useKeyboardShortcuts } from "@redotech/redo-web/keyboard-shortcuts/use-keyboard-shortcuts";
import {
  StatusText,
  UserCallAvailabilityToStatusColor,
} from "@redotech/redo-web/voice/shared/phone-status";
import { filterTruthy } from "@redotech/util/array";
import { localStore } from "@redotech/web-util/storage";
import { memo, ReactNode, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import { KeyboardShortcutsModal } from "../../keyboard-shortcuts/keyboard-shortcuts-modal";
import { autoOnboardingUrl } from "../../onboarding";
import { WALKTHROUGH_REGISTRY } from "../../walkthrough/walkthrough-registry";

const PICKER_WALKTHROUGHS = WALKTHROUGH_REGISTRY.filter(
  (entry) => !entry.hideFromPicker,
);

export const UserProfileService = memo(function UserProfileService({
  children,
}: {
  children: ReactNode;
}) {
  const user = useRequiredContext(UserContext);

  const team = useRequiredContext(TeamContext);

  const auth = useContext(AuthContext);

  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  const { availabilityStatus, setAvailabilityStatus } = useRequiredContext(
    UserCallAvailabilityContext,
  );

  const [availabilityStatusPickerOpen, setAvailabilityStatusPickerOpen] =
    useState(false);

  const [walkthroughPickerOpen, setWalkthroughPickerOpen] = useState(false);

  const [keyboardShortcutsOpen, setKeyboardShortcutsOpen] = useState(false);

  useKeyboardShortcuts({
    bindings: [
      {
        shortcut: globalShortcuts.OPEN_KEYBOARD_SHORTCUTS,
        handler: () => setKeyboardShortcutsOpen(true),
      },
    ],
    scope: "global",
  });

  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLElement | null>(
    null,
  );

  const routes = useMerchantRoutes();

  const voiceEnabled = !!team?.settings.support?.voice?.enabled;

  const settingsUrl = routes.getSettingsSubRoute("personal/profile");

  const intercom = useIntercom();
  const navigate = useNavigate();

  const [deviceAuthToken] = useExpiringLocalStorage<string | undefined>(
    DEVICE_AUTHENTICATION_TOKEN_KEY(team._id),
    undefined,
  );

  const handleLogout = useHandler(() => {
    if (team) {
      teamAuthStore(team._id).set(undefined);
      localStore(TOKEN_KEY).set(undefined);
    } else {
      localStorage.clear();
    }
    intercom.shutdown();

    if (deviceAuthToken) {
      location.href = location.origin + "/login/add";
    } else {
      location.href = `https://${team.storeUrl}/admin`;
    }
  });

  const profileImageUrl = useMemo(() => {
    return getUserAvatarUrl({
      email: user.email || "",
      size: 48,
      userId: user._id,
    });
  }, [user]);

  const dropdownItems: RedoListItem<() => void>[] = useMemo(() => {
    const availabilityItem: RedoListItem<() => void> = {
      id: "availability",
      type: "text",
      text: StatusText[availabilityStatus],
      TrailingIcon: ArrowRightIcon,
      leadingItem: {
        type: "status",
        color: UserCallAvailabilityToStatusColor[availabilityStatus],
      },
      value: () => {
        setProfileDropdownOpen(false);
        setAvailabilityStatusPickerOpen(true);
      },
    };

    const profileItem: RedoListItem<() => void> = {
      id: "profile",
      type: "text",
      text: "Profile",
      leadingItem: { type: "icon", Icon: TeamMemberIcon },
      value: () => {
        navigate(settingsUrl);
      },
    };

    const onboardingItem: RedoListItem<() => void> | undefined = auth
      ? {
          id: "onboarding",
          type: "text",
          href: autoOnboardingUrl(auth.token, auth.teamId).toString(),
          text: "Onboarding",
          leadingItem: { type: "icon", Icon: ArrowRightIcon },
          value: () => {
            location.href = autoOnboardingUrl(
              auth.token,
              auth.teamId,
            ).toString();
          },
        }
      : undefined;

    const walkthroughEnabled =
      team?.featureFlags[OnboardingFlagKey.MERCHANT_WALKTHROUGH] === true;

    const walkthroughItem: RedoListItem<() => void> | undefined =
      walkthroughEnabled && PICKER_WALKTHROUGHS.length > 0
        ? {
            id: "walkthrough",
            type: "text",
            text: "Walkthrough",
            leadingItem: { type: "icon", Icon: CursorClickIcon },
            value: () => {
              setProfileDropdownOpen(false);
              setWalkthroughPickerOpen(true);
            },
          }
        : undefined;

    const keyboardShortcutsItem: RedoListItem<() => void> = {
      id: "keyboard-shortcuts",
      type: "text",
      text: "Keyboard shortcuts",
      leadingItem: { type: "icon", Icon: KeyboardIcon },
      value: () => {
        setProfileDropdownOpen(false);
        setKeyboardShortcutsOpen(true);
      },
    };

    const logoutItem: RedoListItem<() => void> = {
      id: "logout",
      type: "text",
      text: "Logout",
      leadingItem: { type: "icon", Icon: LogOut04 },
      value: handleLogout,
    };

    return filterTruthy([
      voiceEnabled && availabilityItem,
      profileItem,
      onboardingItem,
      walkthroughItem,
      keyboardShortcutsItem,
      logoutItem,
    ]);
  }, [
    auth,
    handleLogout,
    navigate,
    settingsUrl,
    team,
    voiceEnabled,
    availabilityStatus,
  ]);

  const walkthroughItems: RedoListItem<string>[] = useMemo(() => {
    return PICKER_WALKTHROUGHS.map((entry) => ({
      id: entry.id,
      type: "text" as const,
      text: entry.name,
      value: entry.id,
      ...(entry.Icon && {
        leadingItem: { type: "icon" as const, Icon: entry.Icon },
      }),
    }));
  }, []);

  const statusItems: RedoListItem<UserCallAvailability>[] = useMemo(() => {
    const options = Object.values(UserCallAvailability).filter(
      (avail) => avail !== UserCallAvailability.IN_CALL,
    );

    return options.map<RedoListItem<UserCallAvailability>>((availability) => {
      return {
        id: availability,
        type: "text",
        leadingItem: {
          type: "status",
          color: UserCallAvailabilityToStatusColor[availability],
        },
        text: StatusText[availability],
        value: availability,
      };
    });
  }, []);

  const profileData: NavigationUserProfile = {
    name: user.name,
    teamName: team.name,
    setDropdownAnchor,
    setProfileDropdownOpen,
    profileImageUrl,
    status: callAvailabilityStatusToColor[availabilityStatus],
    profileDropdownOpen,
  };

  return (
    <UserProfileContext.Provider value={profileData}>
      <RedoListDropdown<() => void>
        darkMode
        dropdownAnchor={dropdownAnchor}
        dropdownOpen={profileDropdownOpen}
        items={dropdownItems}
        itemSelected={({ item }) => {
          item.value();
        }}
        setDropdownOpen={setProfileDropdownOpen}
        size="sm"
      />
      <RedoListDropdown
        darkMode
        dropdownAnchor={dropdownAnchor}
        dropdownOpen={availabilityStatusPickerOpen}
        items={statusItems}
        itemSelected={({ item }) => {
          setAvailabilityStatus(item.value);
        }}
        setDropdownOpen={(open) => {
          setAvailabilityStatusPickerOpen(open);
          if (!open) {
            setProfileDropdownOpen(true);
          }
        }}
        size="sm"
      />
      <RedoListDropdown<string>
        darkMode
        dropdownAnchor={dropdownAnchor}
        dropdownOpen={walkthroughPickerOpen}
        items={walkthroughItems}
        itemSelected={({ item }) => {
          navigate(
            `/stores/${team._id}/home/overview?walkthrough=${item.value}`,
          );
        }}
        setDropdownOpen={setWalkthroughPickerOpen}
        size="sm"
      />
      <KeyboardShortcutsModal
        isOpen={keyboardShortcutsOpen}
        onClose={() => setKeyboardShortcutsOpen(false)}
      />
      {children}
    </UserProfileContext.Provider>
  );
});

const callAvailabilityStatusToColor: Record<
  UserCallAvailability,
  StatusDotColor
> = {
  [UserCallAvailability.AVAILABLE]: "green",
  [UserCallAvailability.UNAVAILABLE]: "red",
  [UserCallAvailability.IN_CALL]: "yellow",
};
